/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { AgentStatus, AgentStatusRecord } from 'CalltakingCoreApi';
import { SystemReasonCode } from '../model/system-reason-code';
import { AcdReadyStatus, RequestedAgentStatus } from '../model/requested-agent-status';

// Each action requiring separate agent status reported in ACD component or monitoring tab
// should have a system reason code mapping. If a new action is added, it should be added here as well.
export const SYSTEM_REASON_CODE = {
    abandoned: { code: 'ABANDONED', fallback: 'Abandoned' },
    login: { code: 'LOGIN', fallback: 'Login' },
    readyManualChange: { code: 'MANUAL_CHANGE', fallback: 'Manual change' },
    notReadyManualChange: { code: 'MANUAL_CHANGE_NOT_READY', fallback: 'Manual change' },
    noAnswer: { code: 'NO_ANSWER', fallback: 'No Answer' },
    noHeadset: { code: 'NO_HEADSET', fallback: 'No Headset' },
    observing: { code: 'OBSERVING', fallback: 'Observing' },
    observingEnded: { code: 'OBSERVING_ENDED', fallback: 'Observing Ended' },
    onAcdCall: { code: 'ON_ACD_CALL', fallback: 'On ACD Call' },
    onCall: { code: 'ON_CALL', fallback: 'On Call' },
    readyCallEnded: { code: 'CALL_ENDED', fallback: 'Call Ended' },
    notReadyCallEnded: { code: 'CALL_ENDED_NOT_READY', fallback: 'Call Ended' },
    pcp: { code: 'PCP', fallback: 'PCP' },
    pcpEnded: { code: 'PCP_ENDED', fallback: 'PCP Ended' },
    assignedCall: { code: 'ASSIGNED_CALL', fallback: 'Assigned call' },
    holding: { code: 'HOLDING_CALL', fallback: 'Holding call' },
    unknown: { code: 'UNKNOWN', fallback: 'Unknown' }
} as const;

export type SystemReasonCodeKey = (typeof SYSTEM_REASON_CODE)[keyof typeof SYSTEM_REASON_CODE];

// Each CTC internal agent status should have default system reason code mapping.
// If a new status is added, it should be added here as well.
const STATUS_TO_SYSTEM_REASON_CODE: Record<AgentStatus, SystemReasonCodeKey> = {
    ASSIGNED_ABANDONED_CALL: SYSTEM_REASON_CODE.abandoned,
    ON_ACD_CALL: SYSTEM_REASON_CODE.onAcdCall,
    ON_CALL: SYSTEM_REASON_CODE.onCall,
    READY: SYSTEM_REASON_CODE.readyManualChange,
    NOT_READY: SYSTEM_REASON_CODE.login,
    PHONE_UNAVAILABLE: SYSTEM_REASON_CODE.noHeadset,
    WRAPUP: SYSTEM_REASON_CODE.pcp,
    UNKNOWN: SYSTEM_REASON_CODE.unknown,
    HOLDING_CALL: SYSTEM_REASON_CODE.holding,
    ASSIGNED_CALL: SYSTEM_REASON_CODE.assignedCall
};

export function findSystemReasonCode(codes: SystemReasonCode[] | null | undefined, code: string): SystemReasonCode | undefined {
    return (codes ?? []).find((reason) => reason.code === code);
}

export function findSystemReasonLabel(codes: SystemReasonCode[] | null | undefined, code: string | SystemReasonCodeKey, fallback?: string): string {
    const codeStr = typeof code === 'string' ? code : code.code;
    const internalFallback = typeof code === 'string' ? '' : code.fallback;
    return findSystemReasonCode(codes, codeStr)?.effectiveLabel ?? fallback ?? internalFallback;
}

export function systemReasonLabelFromStatus(
    status: AgentStatus,
    codes: SystemReasonCode[] | null | undefined
): string {
    const code = STATUS_TO_SYSTEM_REASON_CODE[status]
    return findSystemReasonLabel(codes, code);
}

export function resolveComputedStatusReasonLabel(
    requestedStatus: AgentStatus,
    currentStatus: AgentStatus,
    requestedReasonLabel: string | undefined,
    currentReasonLabel: string | undefined,
    codes: SystemReasonCode[] | null | undefined
): string | undefined {
    const manualChangeReason = findSystemReasonLabel(
        codes,
        requestedStatus === AcdReadyStatus.READY ? SYSTEM_REASON_CODE.readyManualChange : SYSTEM_REASON_CODE.notReadyManualChange
    );
    const callEndedReason = findSystemReasonLabel(
        codes,
        requestedStatus === AcdReadyStatus.READY ? SYSTEM_REASON_CODE.readyCallEnded : SYSTEM_REASON_CODE.notReadyCallEnded
    );

    const requestedReasonCanBeReplaced = !requestedReasonLabel || requestedReasonLabel === manualChangeReason;

    const shouldApplyCallEndedReason =
        requestedStatus === AcdReadyStatus.READY &&
        requestedReasonCanBeReplaced &&
        (['ON_ACD_CALL', 'ON_CALL'].includes(currentStatus) ||
            (currentStatus === AcdReadyStatus.READY && currentReasonLabel === callEndedReason));

    if (shouldApplyCallEndedReason) {
        return callEndedReason;
    }

    return requestedReasonLabel || systemReasonLabelFromStatus(requestedStatus, codes);
}

export function normalizeComputedAgentStatus(
    requestedStatus: RequestedAgentStatus,
    currentAgentStatus: AgentStatusRecord | null | undefined,
    systemReasonCodes: SystemReasonCode[] | null | undefined
): RequestedAgentStatus {
    const statusReasonDisplayName = resolveComputedStatusReasonLabel(
        requestedStatus.status,
        currentAgentStatus?.status ?? 'UNKNOWN',
        requestedStatus.statusReasonDisplayName,
        currentAgentStatus?.statusReasonDisplayName,
        systemReasonCodes
    );

    return {
        ...requestedStatus,
        statusReasonDisplayName
    };
}
